import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import { useUser } from '@app/store/rtk.user.selectors';
import { ICard } from '@interfaces/_billing';
import { STATES } from '@components/common/helpers/state-helper';
import colors from '@styles/colors';
import Loader from '@components/common/presentational/loader/loader';
import ProfileService from '@v2/services/profile.service';
import { setEntities } from '@app/store/rtk.entities.store';
import { useFormCallbacks, useInitForm } from '@v2/hooks/form';
import { useFormState } from '@app/store/rtk.form.selectors';
import { validateAddress, validateCity, validateRequired } from '@v2/components/common/helpers/field-validation';

// https://stripe.com/docs/stripe-js/react
// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(process.env.STRIPE_KEY);

function Content({ cardDefault, hide }: { cardDefault: ICard, hide: Function }) {
    useInitForm('card', cardDefault);
    const dispatch = useDispatch();
    const allStates = STATES;

    const stripe = useStripe();
    const elements = useElements();
    const user = useUser();
    const data = useFormState('card') || cardDefault;

    const { updateTextInput, updateValue } = useFormCallbacks('card');

    const { firstName, lastName, address, city, state, postcode } = data;
    const { register, handleSubmit, formState: {errors}, control, clearErrors } = useForm({ reValidateMode: 'onBlur' });
    const [stripeError, setStripeError] = useState(null);
    const [loading, setLoading] = useState(false);
    const errorMessage = 'There was an error adding your payment method. Please try again.';

    const createOptions = () => {
        return {
            style: {
                base: {
                    fontSize: '16px',
                    fontWeight: 600,
                    color: colors.NAVY,
                    letterSpacing: '0.01em',

                    '::placeholder': {
                        color: colors.BLUE_LIGHT,
                    },
                },

                complete: {
                    color: colors.NAVY,
                    '::placeholder': {
                        color: colors.BLUE_LIGHT,
                    },
                },

                empty: {
                    color: colors.NAVY,
                    '::placeholder': {
                        color: colors.BLUE_LIGHT,
                    },
                },

                invalid: {
                    color: '#FC4D38',
                },
            },
        };
    };

    function handleStripeChange(event: any) {
        if (event.error) {
            setStripeError(event.error.message
                ? event.error.message
                : 'Invalid card details'
            );
        }
    }

    async function onSubmit() {
        clearErrors();

        if (!stripe || !elements) {
            setStripeError(errorMessage);
            return;
        }

        const ownerInfo = {
            type: 'card',
            owner: {
                name: firstName + ' ' + lastName,
                email: (user && user.email) ? user.email : '',
                address: {
                    line1: address,
                    city: city,
                    state: state ? state.value : null,
                    postal_code: postcode,
                    country: 'US',
                }
            },
        };

        stripe.createSource(elements.getElement(CardElement), ownerInfo)
            .then((result) => {
                setLoading(true);

                if (!result.source || !result.source.id || result.error) {
                    setStripeError((result.error && result.error.message)
                        ? result.error.message
                        : errorMessage
                    );
                    setLoading(false);

                    return;
                }

                updateValue({'stripeSourceId': result.source.id});

                ProfileService.addCard(result.source.id)
                    .then((response: any) => {
                        if (response.card) {
                            dispatch(setEntities({'card': response.card}));
                            hide();
                        } else {
                            setStripeError(errorMessage);
                        }
                    })
                    .catch(async (response: any) => {
                        const data = await response.json();

                        setStripeError(data.message);
                    })
                    .finally(() => setLoading(false));
            })
            .catch(console.error);
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <h2 className="mb-3">Update card</h2>
            <label className={errors.firstName ? 'error' : null}>
                First Name
                <input
                    type="text"
                    name="firstName"
                    aria-label={'first name'}
                    aria-required="true"
                    value={firstName}
                    placeholder="First name"
                    {...register('firstName', {
                        onChange: updateTextInput('firstName'),
                        validate: {
                            value: (value: string) => value && value.trim() !== ''
                                ? true
                                : 'This field is required'
                        }
                    })} />
                <div className="error-message"><ErrorMessage errors={errors} name="firstName" /></div>
            </label>
            <label className={errors.lastName ? 'error' : null}>
                Last Name
                <input
                    type="text"
                    name="lastName"
                    aria-label={'last name'}
                    aria-required="true"
                    value={lastName}
                    placeholder="Last name"
                    {...register('lastName', {
                        onChange: updateTextInput('lastName'),
                        validate: {
                            value: (value: string) => value && value.trim() !== ''
                                ? true
                                : 'This field is required'
                        }
                    })} />
                <div className="error-message"><ErrorMessage errors={errors} name="lastName" /></div>
            </label>
            <label className={errors.address ? 'error' : null}>
                Address
                <input
                    type="text"
                    name="address"
                    aria-label={'address'}
                    aria-required="true"
                    value={address}
                    placeholder="Address line 1"
                    {...register('address', {
                        onChange: updateTextInput('address'),
                        validate: {
                            required: validateRequired,
                            address: validateAddress,
                        }
                    })} />
                <div className="error-message"><ErrorMessage errors={errors} name="address" /></div>
            </label>
            <label className={errors.city ? 'error' : null}>
                City
                <input
                    type="text"
                    name="city"
                    aria-label={'city'}
                    aria-required="true"
                    value={city}
                    placeholder="Enter city"
                    {...register('city', {
                        onChange: updateTextInput('city'),
                        validate: {
                            required: validateRequired,
                            city: validateCity,
                        }
                    })} />
                <div className="error-message"><ErrorMessage errors={errors} name="city" /></div>
            </label>
            <label className={errors.state ? 'error' : null}>
                State
                <Controller
                    control={control}
                    name="state"
                    aria-label={'state'}
                    aria-required="true"
                    defaultValue={state}
                    rules={{ required: 'This field is required' }}
                    render={({ field: { onChange, onBlur, value }}) => (
                        <Select
                            options={allStates}
                            value={allStates.find(obj => obj.value === state)}
                            onBlur={onBlur}
                            onChange={v => {
                                updateValue({state: v});
                                onChange(v);
                            }}
                            className="select-dropdown"
                            classNamePrefix="select" />
                    )} />
                <div className="error-message"><ErrorMessage errors={errors} name="state" /></div>
            </label>
            <label>
                Card Details
                <span className="stripe-input">
                    <CardElement
                        {...createOptions()}
                        onChange={handleStripeChange}
                    />
                </span>
                <div className="error-message">{stripeError}</div>
            </label>

            <button type="submit" className="button primary blue width100" disabled={!stripe}>Add card</button>
            {loading && <Loader overlay={true} />}
        </form>
    );
}


export default function StripeCardModal({ card: cardDefault, hide }: { card: ICard, hide: Function }) {
    return (
        <Elements
            stripe={stripePromise}
            options={{
                fonts: [
                    //{cssSrc: "https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@700&family=Poppins:wght@600&display=swap"},
                    {cssSrc: "https://use.typekit.net/uka0won.css"},
                ]
            }}>
            <Content cardDefault={cardDefault} hide={hide} />
        </Elements>
    )
}